import { Badge, Button, Card, CardHeader, EmptyState, Table, formatDate, formatTime, type Column } from "@helmr/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { staffMonth, type AttendanceDay } from "../../api/attendance";
import type { StaffMember } from "../../api/staff";
import { useServices } from "../../app/services";
import { Failure, RowsSkeleton, styles as c, useScope } from "../classes/common";
import { cellKind, DayEditDrawer, PunchDrawer, STATUS_LABEL } from "./attendanceBits";
import { monthLabel, shiftMonth } from "./month";
import s from "./staff.module.css";

export function StaffAttendanceTab({ staff }: { staff: StaffMember }) {
  const { client } = useServices();
  const sc = useScope();
  const [month, setMonth] = useState(sc.date.slice(0, 7));
  const [edit, setEdit] = useState<AttendanceDay | null>(null);
  const [punching, setPunching] = useState(false);
  const q = useQuery({ queryKey: ["attendance", ...sc.key, staff.id, month], queryFn: () => staffMonth(client, staff.id, month), retry: false });
  const canEdit = sc.ctx.has("staff_attendance.correct");
  const t = (ms: number | null) => (ms === null ? "—" : formatTime(ms, sc.ctx.timeZone));
  const cols: Column<AttendanceDay>[] = [
    { key: "date", header: "Date", cell: (d) => formatDate(d.date, sc.ctx.locale, "weekday") },
    { key: "status", header: "Status", cell: (d) => <span className={`${s.pill} ${s[`s_${cellKind(d)}`]}`}>{STATUS_LABEL[cellKind(d)] ?? d.status}</span> },
    { key: "in", header: "In", cell: (d) => t(d.firstIn) },
    { key: "out", header: "Out", cell: (d) => (d.open ? "Still in" : t(d.lastOut)) },
    { key: "note", header: "Note", cell: (d) => <span className={c.trunc} title={d.overrideReason ?? ""}>{d.overrideReason ?? ""}</span> },
  ];
  return (
    <Card aria-label="Attendance">
      <CardHeader title="Attendance" subtitle={monthLabel(month, sc.ctx.locale)}
        action={<div className={c.row}><Button size="sm" aria-label="Previous month" onClick={() => setMonth(shiftMonth(month, -1))}>‹</Button><Button size="sm" aria-label="Next month" onClick={() => setMonth(shiftMonth(month, 1))}>›</Button>{canEdit ? <Button size="sm" variant="primary" onClick={() => setPunching(true)}>Add missed punch</Button> : null}</div>} />
      {q.isPending ? <RowsSkeleton /> : q.error ? <Failure error={q.error} title="Could not load attendance" onRetry={() => void q.refetch()} /> : (
        <>
          <div className={s.summary}>
            <span><b>{q.data.summary.presentDays}</b> present</span><span><b>{q.data.summary.lateCount}</b> late</span><span><b>{q.data.summary.absentDays}</b> absent</span>
            <span><b>{q.data.summary.leaveDays}</b> leave</span>{!q.data.summary.scheduled ? <Badge tone="warn">No schedule</Badge> : null}
          </div>
          {q.data.days.length === 0 ? <EmptyState compact icon="clock" title="No attendance recorded this month" /> : <Table label="Days" columns={cols} rows={q.data.days} rowKey={(d) => d.date} onRowClick={canEdit ? setEdit : undefined} minWidth={480} />}
        </>
      )}
      {edit ? <DayEditDrawer staffId={staff.id} name={staff.name} date={edit.date} current={edit} onClose={() => setEdit(null)} /> : null}
      {punching ? <PunchDrawer staffId={staff.id} name={staff.name} homeBranchId={staff.homeBranchId} defaultDate={sc.date} onClose={() => setPunching(false)} /> : null}
    </Card>
  );
}
